/* eslint-disable valid-jsdoc */

/**
 * Visa SRC System Adapter
 *  - The Visa SRC System Adapter is the interface used by Visa Checkout to communicate
 *    with payment application JavaScript APIs & SRC-initiator.
 *  - The SRC System Adapter object implementation are expected to be under
 *    `window.vAdapters.VisaSRC` namespace
 *  - WIP - Most part of the system SDK design will change. Waiting for the Arch approval
 *    to proceed ahead with the proposed re-design.
 *  - Code Review Alert
 *    * Most of the code here are not production ready.
 *    * We will be adding test cases in upcoming sprints
 *    * Documentation needs to be added for all the functions
 */

import { getCookie } from '@visa/lib-cookie';
import {
  ACCT_INACCESSIBLE,
  AUTH_ERROR,
  INVALID_PARAMETER,
  OTP_SEND_FAILED,
  UNKNOWN_ERROR
} from './constants/api-error-enums';
import { CHECKOUT_ERRORS, UNABLE_TO_CONNECT } from './constants/checkout-error-enums';
import {
  ACCEPTED,
  ACCEPT_NEW_TERMS,
  B2CIAM_ACCEPT_NEW_TERMS,
  B2CIAM_ENUMS,
  CARD_ENROLL_EVENTS,
  CARD_ENROLL_FAILURES,
  DEFAULT_USER_TYPE,
  FLOW_TYPE,
  NOT_ACCEPTED,
  NOTEXIST,
  NOTPRESENT,
  OTP_SUCCESS_VALIDATION_CHANNEL,
  PRESENT,
  THM_ORG_ID,
  UNDEFINED,
  USERNOTALLOWED,
  X_USER_CRED
} from './constants/common-enums';
import * as COMPLIANCE_ENUMS from './constants/compliance-settings-enums';
import { REMEMBER_ME } from './constants/cookie';
import { PAYMENT_SETTING, WITHIN_CHECKOUT } from './constants/custom-flow-type-enums';
import SRC_DCF_CONSTANTS, { DCF_TIMEOUT_RESET } from './constants/dcf-app';
import { BUTTON_ACTION, DEFAULT_VALUE } from './constants/external-sdk-maps';
import { ADD_CARD, CARD_LIST, REVIEW } from './constants/initiator-flow-enums';
import { WELCOME_TABS, STARTING_TAB } from './constants/local-storage-enum';
import SRC_SYSTEM from './constants/src-system';
import * as EVENTS from './constants/system-external-events';
import { ACCESS_TOKEN } from './constants/unbind-app-instance';
import Browser from './helpers/browser';
import canUseSessionStorage from './helpers/can-use-session-storage.js';
import collapsedlogger from './helpers/collapsed-logger';
import ConvertApiErrorClass from './helpers/convert-api-error-class';
import getDataToMerchant from './helpers/data-to-merchant';
import enrollCardHelper from './helpers/enroll-card-helper';
import filterTokens from './helpers/filter-tokens';
import fixMessage from './helpers/fix-message';
import { systemCorrelationId, systemSessionId } from './helpers/gen-static-id';
import getCardListExternal from './helpers/get-card-list-external';
import getCheckoutError from './helpers/get-checkout-error';
import getCheckoutMessage from './helpers/get-checkout-message';
import getClientId from './helpers/get-client-id';
import getCountryCodeByISO from './helpers/get-country-by-iso-code';
import getDismissAppMessage from './helpers/get-dismiss-app-message';
import getExternalClientId from './helpers/get-external-client-id';
import getIsSplunkLoggingEnabled from './helpers/get-is-splunk-logging-enabled';
import getLockOutFlowForB2CIAMApis from './helpers/get-lock-out-flow-for-b2ciam-apis';
import logButtonClick from './helpers/get-log-button';
import { getNamespace } from './helpers/get-namespace';
import getRouteUserInDCFMessage from './helpers/get-route-user-in-dcf-message';
import { getSupportedLocales } from './helpers/get-supported-locales';
import GtmHelper from './helpers/gtm';
import { mark, marks } from './helpers/instrumentation';
import isEmptyObject from './helpers/is-empty-object';
import isEmptyString from './helpers/is-empty-string';
import isAccountDeviceLocked from './helpers/is-locked';
import * as LocalStorageUtil from './helpers/local-storage';
import logger from './helpers/logger';
import mapMerchantConfig from './helpers/map-merchant-config';
import parseIdToken from './helpers/parse-id-token';
import PostMessage from './helpers/post-message';
import { getUsername } from './helpers/rememberme-cookie-helper';
import SplunkLoggingSingleton from './helpers/splunk-logging-singleton';
import StaticIdSingletonClass from './helpers/static-id';
import * as unbindHelpers from './helpers/unbind-app-instance';
import until from './helpers/until';
import validateAssuranceData from './helpers/validate-assurance-data';
import validateCompleteIdentityValidationData from './helpers/validate-completeIdentityValidation-data';
import validateComplianceSettings from './helpers/validate-compliance-settings';
import validateDpaTransactionOptions from './helpers/validate-dpaTransactionOptions-data';
import validateIdLookupData from './helpers/validate-idlookup-data';
import validateInitData from './helpers/validate-init-data';
import VCOPUtils from './helpers/vcop-utils';
import DcfLaunchData from './lib/dcf/launch-data';
import { getIdToken } from './lib/id-token';
import PushEventsService from './lib/services/cal-event-logging';
import CardBinsService from './lib/services/cardbins-api';
import { fetchThmHeaderHint } from './lib/services/device-api';
import AccessTokenService from './lib/services/generate-access-token-api';
import IdLookupService from './lib/services/id-lookup-api';
import IntentService from './lib/services/intent-api';
import OTPB2CIAMService from './lib/services/otp-b2ciam-api';
import PaymentDataService from './lib/services/payment-data-api';
import RecognizeService from './lib/services/recognize-api';
import SpawnTokenService from './lib/services/spawn-token-api';
import {
  isErmEnabled,
  getUsernameFromErmCookie,
  setErmActivityToken
} from './lib/ssi-erm/check-erm';
import { getUsernameFromSsiCookie, hasValidStaySignedIn } from './lib/ssi-erm/check-ssi';
import { deleteSsiErmCookies, setErmEnabled } from './lib/ssi-erm/ssi-erm-cookie-manager';
import initiateTHM from './lib/thm/thm-launch';
const DATA_DELIM = '--'; // delimiter within a postMessage

/**
 * The following 2 variables were added as part of F20000. We now are launching the application
 * the moment `checkout()` function is invoked. This can result in the application launching and
 * requesting config data before the config data is ready for consumption by the app.
 */
let didDcfAskForConfigs = false;
let isConfigsReadyForDcf = false;
let thmProfilingComplete = false;
let srciLoaded = false;

/* eslint-disable no-unused-vars */
const _getValidDCFConfig = config => {
  const { middleware, queryParamsRaw, routes, ...picked } = config;
  return picked;
};
/* eslint-enable no-unused-vars */

const _getSrciDomain = () => {
  const params = Browser.extractQueryParams();
  const parentUrl = (params && params.parentUrl) || '';
  return decodeURIComponent(parentUrl);
};

PostMessage.init({ parentUrl: _getSrciDomain() });

/**
 * Visa SRC-I requires actual api response to handle TnC and other logic
 *  - This function will help in setting up the customData in the response object
 * @param {object} data
 * @param {object} response
 * @returns {object} data
 */
const setCustomData = (data, response) => {
  if (StaticIdSingletonClass.visaSrci && !isEmptyObject(data)) {
    Object.assign(data, { customData: response });
  }

  return data;
};

/**
 * Send Recognized User response as postMessage
 * @param {number} accessTokenExpiresIn
 * @param {object|null} error api errorObject for either Activity/sidToken/spawn or /recognized
 * @param {boolean} isB2CIAMRecognized if /recognized api return recogized response
 * @param {object|null} isRecognizedResponse api response for Activity(ERM) or sidToken(SSI) or /recognized
 * @param {object} message
 * @param {string} pId
 * @param {object|null} spawnResponse Response for spawn API. Used for grabbing idTokens.
 *
 * TODO: refactor this function out of system-sdk-external into a helper file that can be easily tested
 */
const sendRecognizedUserResponse = ({
  accessTokenExpiresIn = 0,
  error,
  isB2CIAMRecognized = false,
  isRecognizedResponse,
  isVsb,
  message,
  pId,
  spawnResponse
}) => {
  const data = isEmptyObject(message.data) ? {} : message.data;
  const isValidB2CIARecognized = isB2CIAMRecognized && isRecognizedResponse.ok;
  data.recognized =
    (!error && (hasValidStaySignedIn() || isErmEnabled())) || isValidB2CIARecognized || false;
  if (isVsb || StaticIdSingletonClass.visaSrci) {
    data.usedDevice = Boolean(LocalStorageUtil.getItem(STARTING_TAB));
  }
  // ERM/SSI API's, if successful, are followed by a spawn API. Validate the most
  // recent one to determine if we should send back an error.
  const response = isEmptyObject(spawnResponse) ? isRecognizedResponse : spawnResponse;
  const isNoCookiesRecognizeResponse =
    !isB2CIAMRecognized && response.ok && isEmptyObject(response.data);
  if ((response && !response.ok) || isNoCookiesRecognizeResponse) {
    if (error && error.reason) {
      data.error = error;
    }
  } else {
    const issuedTokens = isRecognizedResponse.data?.issued_tokens || [];
    data.idTokens = [];
    // Attach idToken from spawn API to final response for legacy isRecognized flow
    const idToken = getIdToken(issuedTokens);
    if (idToken) {
      data.idTokens.push(idToken);
    }

    /**
      * Need to set a timestamp for when the token was created so the app can calculate how long
      the user was on the merchant page before launching the app.
      * The app needs to start its session timer once it is initiated.
      */
    if (!isRecognizedResponse || typeof isRecognizedResponse !== 'object') {
      isRecognizedResponse = {};
    }

    if (typeof isRecognizedResponse?.data !== 'object') {
      isRecognizedResponse.data = {};
    }
    // VCO-11259 - SRC-I App looks for expires_in and based on this the session warning is determined
    if (isValidB2CIARecognized) {
      isRecognizedResponse.data.expires_in = accessTokenExpiresIn;
      isRecognizedResponse.data.creation_timestamp = Date.now();
    }
  }
  data.pId = pId;
  message.data = setCustomData(data, isRecognizedResponse);
  logger('sendRecognizedUserResponse -- message =', JSON.stringify(message));
  PostMessage.send(message);
  mark(marks.isRecognizedComplete);
};

export const Adapter = {
  /**
   * Return true/false whether vdcp configs are being updated by checking for existence of config ids
   *
   * @return {boolean}
   */
  areVDCPConfigsUpdating() {
    return Boolean(Object.keys(this.vdcpConfigsToUpdate).length);
  },
  /**
   * 3.8 Checkout
   *
   * For more information see `/docs/SYSTEM_SDK_EXTERNAL.md`
   *
   * @param {object} xoInfo Checkout Information/data
   */
  async checkout(xoInfo) {
    logger('PSP Data SYS-SDK >> ', xoInfo);
    /**
     * TODO: Do we need to save it to DCF-Launch Data ??
     * Visa SRC-I will pass an overridering isCardFirstFlow flag to avoid the edge case
     * Edge case: when user tries an idlookup and then CFE flow, there will be an temp accessToken in
     * System sdk. Refer VCO-11113
     */
    const {
      assuranceData,
      cardBrand,
      complianceSettings,
      consumer,
      dataLayer = {},
      dpaTransactionOptions,
      // truthy encryptedCard means user is in enrollCard flow
      encryptedCard,
      idToken,
      isCardFirstFlow,
      isSRCBranded,
      pId,
      routeBeforeLaunchDcf,
      // truthy srcDigitalCardId means user is in selectCard flow
      srcDigitalCardId
    } = xoInfo;
    let message;
    let dpaTransactionOptionsError = null;
    const externalProfileId = this.config?.merchantConfig?.externalProfileId || 'SYSTEMDEFAULT';
    const hasDpaTransactionOptions = !isEmptyObject(dpaTransactionOptions);

    isConfigsReadyForDcf = false;
    didDcfAskForConfigs = false;
    /*
     * VCO-14476 reset the route so that app knows we want to route to DCF,
     * otherwise existing route in configs may be out of date ex: card-list
     */
    this.config.toRoute = undefined;
    // VCO-14146 - set payMode. Will be automatically added to redux state when app loads and calls fetchConfigs
    DcfLaunchData.customProps.payMode = this.config.payMode;
    DcfLaunchData.setRouteBeforeLaunchDcf(routeBeforeLaunchDcf);
    // Add isnewuser attribute to dataLayer for GTM tracking
    dataLayer.isnewuser = isEmptyString(this.accessToken);
    /*
     * VCO-14522 only immediately launch to dcf when external srci to prevent popup blocker,
     * however immediately changing url clears the checkout error listener from Visa SRCi app side,
     * so only make the change when needed
     */
    if (!StaticIdSingletonClass.visaSrci) {
      this.instantiateCheckoutApp({
        dataLayer,
        externalProfileId,
        isSRCBranded,
        pId
      });
      this.config.loaderStartTimestamp = Date.now();
    }

    if (!isEmptyObject(this.nonVisaGtmCustomAttributes)) {
      Object.assign(dataLayer, this.nonVisaGtmCustomAttributes);
    }

    const dataObj = {
      cardBrand,
      consumer,
      dataLayer,
      dpaTransactionOptions,
      encryptedCard,
      externalProfileId,
      idToken,
      isCardFirstFlow,
      isSRCBranded,
      pId,
      srcDigitalCardId
    };

    // throw error when dpaTransactionOptions exists and not an {object}
    if (dpaTransactionOptions && isEmptyObject(dpaTransactionOptions)) {
      this.processDpaTransactionOptionsEmptyObj(dataObj);
      return;
    }

    // Use Joi to do validation when dpaTransactionOptions is an {object}
    if (hasDpaTransactionOptions) {
      dpaTransactionOptionsError = validateDpaTransactionOptions(dpaTransactionOptions);
      if (dpaTransactionOptionsError) {
        message = getCheckoutMessage({ error: dpaTransactionOptionsError, pId });
        PostMessage.send(message);
        // Do not close the app when it is visa-srci on error
        if (!StaticIdSingletonClass.visaSrci) {
          message = getDismissAppMessage();
          PostMessage.send(message);
        }
        logger('Checkout -- sending post message back to Visa SDK ...', message);
        return;
      }
      if (
        dpaTransactionOptions?.customInputData?.customFlowType?.toLowerCase() === PAYMENT_SETTING
      ) {
        this.config.customFlowType = PAYMENT_SETTING;
      } else if (
        dpaTransactionOptions?.customInputData?.customFlowType?.toLowerCase() === WITHIN_CHECKOUT
      ) {
        this.config.customFlowType = WITHIN_CHECKOUT;
      }
    }

    if (assuranceData && Object.keys(assuranceData).length) {
      const assuranceDataError = validateAssuranceData(assuranceData);
      if (assuranceDataError) {
        message = getCheckoutMessage({ error: assuranceDataError, pId });
        PostMessage.send(message);
        if (!StaticIdSingletonClass.visaSrci) {
          message = getDismissAppMessage();
          PostMessage.send(message);
        }
        logger('Checkout -- sending post message back to Visa SDK ...', message);
        return;
      }
    }

    if (!isEmptyObject(assuranceData)) {
      DcfLaunchData.setUserInfo({ assuranceData });
    }

    // F28951 set flags based on complianceSettings only for add card
    if (complianceSettings !== undefined) {
      const complianceSettingsError = validateComplianceSettings(complianceSettings);
      if (complianceSettingsError) {
        message = getCheckoutMessage({ error: complianceSettingsError, pId });
        PostMessage.send(message);
        if (!StaticIdSingletonClass.visaSrci) {
          message = getDismissAppMessage();
          PostMessage.send(message);
        }
        logger('Checkout -- sending post message back to Visa SDK ...', message);
        return;
      }
      for (const complianceTypeObj of complianceSettings.complianceResources) {
        switch (complianceTypeObj?.complianceType) {
          case COMPLIANCE_ENUMS.REMEMBER_ME:
            DcfLaunchData.setUserInfo({ alwaysRememberMe: true });
            break;
          case COMPLIANCE_ENUMS.TERMS_AND_CONDITIONS:
            DcfLaunchData.setUserInfo({ tncStatus: ACCEPTED });
            DcfLaunchData.setUserInfo({ isComplianceTncPassed: true });
            break;
          case COMPLIANCE_ENUMS.PRIVACY_POLICY:
            DcfLaunchData.setUserInfo({ isPrivacyPolicyPresent: true });
            break;
        }
      }
    }

    DcfLaunchData.setUserInfo({
      isVsb: this.config.isVsb,
      isVsbButtonless: this.config.isVsbButtonless
    });
    // F25593 optimized half sheet flags
    DcfLaunchData.setUserInfo({ customFlowType: this.config.customFlowType });
    DcfLaunchData.setUserInfo({
      isOptimizedHalfSheetEligible:
        !Browser.isMobileSafari() &&
        Browser.checkPhoneBrowser() &&
        (this.config.customFlowType === PAYMENT_SETTING ||
          this.config.customFlowType === WITHIN_CHECKOUT)
    });

    if (isEmptyString(srcDigitalCardId) && isEmptyString(encryptedCard)) {
      message = getCheckoutMessage({
        dataLayer,
        error: getCheckoutError(CHECKOUT_ERRORS.SRC_DIGITAL_CARD_ID),
        pId
      });
      PostMessage.send(message);
      // Do not close the app when it is visa-srci on error
      if (!StaticIdSingletonClass.visaSrci) {
        message = getDismissAppMessage();
        PostMessage.send(message);
      }
      logger('Checkout -- sending post message back to Visa SDK ...', message);
      return;
    } else if (
      // VCO-15686 if this is 2nd checkout after select card success, break out here
      !StaticIdSingletonClass.visaSrci &&
      !isEmptyString(srcDigitalCardId) &&
      isEmptyString(this.config.vIntentId)
    ) {
      message = getCheckoutMessage({ error: { reason: UNABLE_TO_CONNECT }, pId });
      PostMessage.send(message);
      message = getDismissAppMessage();
      PostMessage.send(message);
      return;
    }
    if (!isEmptyString(this.accessToken) && !isEmptyString(idToken)) {
      AccessTokenService.revokeTokens(this.accessToken, ['id_token'], [idToken]);
    }
    /**
     * Below are the conditions to use the input IdToken when visa access_token is not present
     *
     * And when the checkout api is invoked as part of enroll/add new card scenario:
     * Ignore for select card use case
     * And when valid IdToken is present
     *
     * NOTE - This may change when Visa supports multiple identity
     */
    if (isEmptyString(this.accessToken) && !isEmptyString(idToken)) {
      try {
        await this.generateAccessTokenFromIdToken(idToken);
      } catch (err) {
        // TODO: Handle errors
      }
    }

    /**
     * if we've made it this far we'll route to DCF.
     * We need to convert our access token into an x-auth-code for
     * the DCF. DCF will use x-auth-code to generate an access token.
     **/
    if (!isEmptyString(this.accessToken) && this.isFullAccessToken) {
      try {
        await this.generateXAuthCodeFromAccessToken(this.accessToken);
      } catch (err) {
        // TODO: Handle errors
      }
    } else if (!this.isFullAccessToken) {
      DcfLaunchData._customProps.userInfo.consumerPresent = false;
      xoInfo.isCardFirstFlow = true;
    }

    // enrollCard flows
    if (!isEmptyString(encryptedCard) && isEmptyString(srcDigitalCardId)) {
      await this.enrollCard(xoInfo);
      return;
    }

    // route dcf for select card, instantiate dcf if we haven't already
    if (StaticIdSingletonClass.visaSrci) {
      this.instantiateCheckoutApp({
        dataLayer,
        externalProfileId,
        isSRCBranded,
        pId
      });

      this.config.loaderStartTimestamp = Date.now();
    }
    this.routeToDcfForSelectCard(dataObj);
  },

  /**
   * 3.6 CompleteIdentityValidation
   *  - based on accessToken, system sdk will invoke verifyOtp api
   * @param {string} data
   * @param {string} pId
   */
  completeIdentityValidation(data, pId) {
    const message = { type: EVENTS.SYS_COMPLETE_IDENTITY_VALIDATION };
    const error = validateCompleteIdentityValidationData(data.validationData);
    if (!error) {
      Adapter.probe(data, pId);
    } else {
      const data = { pId };
      data.error = error;
      logger('completeIdentityValidation -- error', error);
      message.data = data;
      PostMessage.send(message);
    }
  },
  /**
   * Deletes the intent if one exists
   *  - Based on the suppressDeleteIntentApi, system sdk will ignore the delete intent apis
   * @param {string} pId
   * @param {!boolean} suppressDeleteIntentApi
   */
  deleteIntent(pId, suppressDeleteIntentApi = false) {
    const message = { type: EVENTS.SYS_DELETE_INTENT };
    const intentId = this.config.vIntentId;

    // VCO-12571 VCO-14530 Reset system sdk state vIntentId when intent is deleted by app or system-sdk
    this.config.vIntentId = null;

    if (!suppressDeleteIntentApi && intentId) {
      IntentService.cancelIntent(this.accessToken, this.config.apikey, intentId)
        .then(response => {
          const data = response || {};
          data.pId = pId;
          message.data = data;
          PostMessage.send(message);
        })
        .catch(err => {
          const data = err || {};
          data.pId = pId;
          message.error = data;
          PostMessage.send(message);
        });
    } else {
      message.data = { pId };
      PostMessage.send(message);
    }
  },

  /**
   * Added as part of PSP feature. This function will be called if PSP flag available in checkout request.
   * calls enrollCard() to construct call cards/ api and construct message/configs to update, then
   * update this scope, then call handleEnrollCardMessage() to consume message
   * @param {object} xoData
   */
  async enrollCard(xoInfo) {
    const { dataLayer = {}, isSRCBranded, pId } = xoInfo;
    const externalProfileId = this.config?.merchantConfig?.externalProfileId || 'SYSTEMDEFAULT';

    const { configsToUpdate, message } = await enrollCardHelper(Adapter, xoInfo);

    // save configs that need to be updated into Adapter properties
    this.config = { ...this.config, ...configsToUpdate.config };

    if (message.type === EVENTS.ROUTE_USER_IN_DCF) {
      // should fire User Type Identification event for CFE flows where email is being passed
      const pendingEvents =
        DcfLaunchData.customProps?.transfer?.addCardInfo?.enrollCardResponse?.maskedCard
          ?.digitalCardData?.pendingEvents ?? [];
      const maskedConsumer =
        DcfLaunchData.customProps?.transfer?.addCardInfo?.enrollCardResponse?.maskedConsumer;
      const maskedEmail =
        maskedConsumer?.maskedEmail || maskedConsumer?.maskedConsumerIdentity?.maskedIdentityValue;
      const isPresent =
        maskedEmail && pendingEvents.includes(CARD_ENROLL_EVENTS.PENDING_CONSUMER_IDV);
      const gtmData = { isPresent, maskedEmail };
      if (this.config.isCardFirstFlow && maskedEmail) {
        const lookupData = {
          email: maskedEmail,
          event: 'user look up response',
          lookupStatus: isPresent ? PRESENT : NOTPRESENT,
          saveMyInfoWithVisa: isPresent.toString(),
          userFlow: isPresent ? GtmHelper.USER_FLOW.EXISTING : GtmHelper.USER_FLOW.NEW
        };

        PostMessage.send({ data: gtmData, type: EVENTS.SEND_GTM_USER_TYPE_IDENTIFICATION });
        PostMessage.send({ data: lookupData, type: EVENTS.SYS_SEND_USERNAME_LOOKUP_GTM_DATA });
      }
      /*
      * VCO-14522 if Visa SRCi, we did not immediately launch dcf. Now that we
      * know checkout succeeded without errors, launch to dcf
      */
      if (StaticIdSingletonClass.visaSrci) {
        this.instantiateCheckoutApp({
          dataLayer,
          externalProfileId,
          isSRCBranded,
          pId
        });
        this.config.loaderStartTimestamp = Date.now();
      }
      isConfigsReadyForDcf = true;
      if (didDcfAskForConfigs) {
        PostMessage.send(message);
      }
    } else {
      /*
       * VCO-14522 Do not close the app when it is visa-srci on error
       * and send error message to see correct error notification
       */
      if (StaticIdSingletonClass.visaSrci) {
        message.data.error = { reason: CARD_ENROLL_FAILURES.CARD_MISSING };
      }
      PostMessage.send(message);
      const errorMsg = message?.data?.error?.reason ?? '';
      if (!isEmptyString(errorMsg) && !StaticIdSingletonClass.visaSrci) {
        PostMessage.send(getDismissAppMessage());
      }
    }
  },

  /**
   * Return DCF Config from SRC System SDK
   *
   * To avoid possible race conditions with `updateVDCPConfig, `fetchDcfConfigs` will wait for configs to be 'unlocked' before returning the config
   *
   * @param {string} pId
   */
  async fetchDcfConfigs(pId) {
    /**
      * F21350: Add `enabledFeatureToggles` object to `this.config` here because we need to send it as part of configs
      but only if DCF is being launched from an External SRCi.
      * In this case SRC shell will fetch configs from system-sdk instead of merchant sdk (because there would be no
      merchant sdk).
      * This sets the information in `this.config` only if it's needed.
      */
    this.config.enabledFeatureToggles = window.VISA_GLOBALS?.enabledFeatureToggles ?? {};
    collapsedlogger(
      `fetchDcfConfigs -- configs locked: ${this.areVDCPConfigsUpdating()}`,
      Object.keys(this.vdcpConfigsToUpdate)
    );

    // wait for all configs to finish updating before returning the configs
    await until(() => !this.areVDCPConfigsUpdating());
    collapsedlogger('fetchDcfConfigs -- fetching configs');

    const dcfConfig = _getValidDCFConfig(this.config);
    const configs = Object.assign({}, { ...dcfConfig }, DcfLaunchData.customProps, {
      dataLayer: window.dataLayer
        ? Object.assign({}, ...window.dataLayer, {
            event: 'PERSIST',
            event_action: 'PERSIST',
            event_label: 'PERSIST'
          })
        : undefined,
      dpaData: this.dpaData,
      openvv: !StaticIdSingletonClass.visaSrci
    });
    const message = {
      data: { configs, pId },
      type: SRC_DCF_CONSTANTS.EVENTS.DCF_FETCHED_CONFIGS
    };
    PostMessage.send(message);

    // Reset config.why passed when user switches between srci and dcf
    this.resetDCFWhy();
  },

  fetchDeviceId() {
    if (!StaticIdSingletonClass.visaSrci || !thmProfilingComplete || !srciLoaded) {
      return;
    }

    thmProfilingComplete = false;
    srciLoaded = false;

    fetchThmHeaderHint().then(thmProfileHint => {
      if (thmProfileHint) {
        Adapter.config.thmProfileHint = thmProfileHint;
      }
    });
  },

  /**
   * TODO - Will be removed as part of INO implementaiton.
   * Send configs to DCF
   */
  fetchSendConfigsToDCF(pId) {
    const dcfConfig = _getValidDCFConfig(this.config);
    logger('fetchSendConfigsToDCF -- Sending config ...');
    const data = {
      ...dcfConfig,
      pId
    };
    const message = {
      data,
      type: SRC_DCF_CONSTANTS.EVENTS.DCF_FETCHED_CONFIGS
    };
    PostMessage.send(message);
  },

  /**
   * Send configs to DCF RXO
   */
  fetchSendMerchantConfigsToRXO(pId) {
    logger('fetchSendMerchantConfigsToRXO -- Sending merchant-config ...');
    const data = {
      merchantConfig: this.config.merchantConfig,
      pId,
      vInitRequest: this.config.vInitRequest || this.config.merchantConfig
    };
    // building vInitRequest with original MerchantConfig, RXO depends on it!!
    if (!data.vInitRequest.apikey) {
      data.vInitRequest.apikey = this.config.apikey;
    }
    const message = {
      data,
      type: SRC_DCF_CONSTANTS.EVENTS.SET_RXO_MERCHANT_CONFIGS
    };
    PostMessage.send(message);
  },

  /**
   * Generate accessToken from input Non-Visa/Visa IdToken
   * @param {string} idToken
   */
  generateAccessTokenFromIdToken(idToken) {
    return new Promise((resolve, reject) => {
      AccessTokenService.generateAccessTokenFromIdToken(idToken, this.config.apikey, this.sessionId)
        .then(response => {
          if (response.ok) {
            /**
             *  auth_level:
             *  aal1 : => sms_otp/email_otp => do NOT step up the user
             *  aal0 : => cookie/swk => step up the user
             */
            DcfLaunchData.setUserInfo({
              auth_level:
                response?.data?.auth_data?.authenticator_assurances?.[0]?.level ?? undefined
            });
            // VCO-14456 VCO-14457: ensure that app calls GET /intent
            DcfLaunchData.setUserInfo({
              isUserAuthenticated: true
            });
            parseIdToken(idToken);
            this.updateAccessToken(response);
            AccessTokenService.revokeTokens(this.accessToken, ['id_token'], this.idTokens).then(
              () => {
                this.idTokens = new Set();
              }
            );
            this.resetErmActivityToken();
            // Check if the user needs to accept terms & conditions
            // and update dcf launch data to indicate T&C flow
            const responseStatus = response?.data?.data?.probeType?.[0];

            if (responseStatus === ACCEPT_NEW_TERMS || responseStatus === B2CIAM_ACCEPT_NEW_TERMS) {
              this.handleAcceptNewTermsResponse(
                response,
                responseStatus === B2CIAM_ACCEPT_NEW_TERMS
              );
            }
            resolve();
          } else {
            if (isAccountDeviceLocked(response)) {
              DcfLaunchData.setUserInfo({
                isLocked: true
              });
            }
            reject();
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  /**
   * Converts an access token to an x-auth-code for the DCF to use. DCF takes the x-auth-code and generates an access token.
   * @param {string} accessToken
   */
  generateXAuthCodeFromAccessToken(accessToken) {
    return new Promise((resolve, reject) => {
      AccessTokenService.generateXAuthCode(accessToken)
        .then(response => {
          if (response.ok) {
            const xAuthCode = response.data?.access_token; // Backend named the x-auth-code as access_token for some reason :(
            DcfLaunchData.setXAuthCode(xAuthCode);
            resolve(xAuthCode);
          } else {
            /**
             * What happens if we failed to generate an x-auth-code???
             * Should we retry the API? We can't launch the DCF without passing it an x-auth-code correct?
             * Or else the DCF wont be able to generate an access token!
             */
            reject();
          }
        })
        .catch(() => reject());
    });
  },

  getCardBins(pId) {
    const message = { type: EVENTS.SYS_CARD_BINS };
    let cardBins = [];
    CardBinsService.getCardBins()
      .then(response => {
        if (response.ok) {
          cardBins = response.data?.cardBins ?? [];

          const data = {
            pId,
            result: {
              cardBins
            }
          };
          message.data = data;
          PostMessage.send(message);
        }
      })
      .catch(err => {
        const data = err || {};
        data.pId = pId;
        message.error = data;
        PostMessage.send(message);
      });
  },
  /** ERM Activity Reference Id Request
   * Return DCF url
   * @returns {string} domain
   */
  getDcfDomain() {
    const loadDCF = '&loadDcf=true';
    return this.domain + loadDCF;
  },

  /**
   *  3.3 getSrcProfile to get list of cards associated with the userId
   *
   * For more information see `/docs/SYSTEM_SDK_EXTERNAL.md`
   *
   * @param {string} pId
   * @param {array} idTokens
   */
  getSrcProfile(pId, idTokens) {
    DcfLaunchData.customProps.idTokens = idTokens;
    LocalStorageUtil.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);
    const isLocked = DcfLaunchData.customProps.userInfo.isLocked;
    const message = { type: EVENTS.SYS_GET_SRC_PROFILE };
    const setIntentFlag =
      DcfLaunchData.customProps.userInfo.tncStatus !== NOT_ACCEPTED &&
      !isLocked &&
      !this.config.vIntentId;
    const trackSetIntent =
      this.initData.visaSrci ||
      this.accessToken ||
      this.gtmFlags.isRecognizedFired ||
      this.gtmFlags.identityLookupFired;
    /**
     * Per VCO-12758 Expectation for the getSrcProfile input request should be either Array/JSON Object.
     * String input is not a valid request param.
     *
     * VCO-14530 - When user is locked out of DCF system-sdk will delete its internal accessToken so we need
     * to check for this scenario before returning an error
     */
    if (isEmptyString(this.accessToken) && idTokens.length === 0 && !isLocked) {
      const data = { error: { reason: INVALID_PARAMETER } };
      data.pId = pId;
      message.data = data;
      PostMessage.send(message);
      return;
    }
    IntentService.getCardList({
      accessToken: this.accessToken,
      isLocked,
      isVisaSrci: StaticIdSingletonClass.visaSrci,
      merchantInfo: this.config,
      tncStatus: DcfLaunchData.customProps.userInfo.tncStatus,
      trackSetIntent
    })
      .then(response => {
        this.profileCount--;
        const data = { pId };
        // eslint-disable-next-line no-negated-condition
        if (!response.ok) {
          if (this.profileCount === 0) {
            data.error = ConvertApiErrorClass.handleGetSrcProfileError(response);
            message.data = data;
            logger('getSrcProfile -- failure', message);
            PostMessage.send(message);
          } else {
            this.getSrcProfileByIdToken(pId, idTokens, idTokens.length - this.profileCount);
          }
        } else {
          /*
           * F27004 if digitalCardId present as well as id, create a mapping object
           * digitalCardId (vPanEnrollmentId) -> id (paymentInstrumentId) to convert during checkout
           */
          this.piMapping = {};
          if (Array.isArray(response?.data?.paymentInstrument)) {
            for (const card of response.data.paymentInstrument) {
              if (!isEmptyString(card.digitalCardId)) {
                this.piMapping[card.digitalCardId] = card.id;
              }
            }
          }
          const maskedCards = getCardListExternal(response);
          this.__maskedCards = maskedCards;

          if (maskedCards.length === 0 && this.profileCount > 0) {
            this.getSrcProfileByIdToken(pId, idTokens, idTokens.length - this.profileCount);
          } else {
            const srcCorrelationId = response?.data?.intentId ?? this.config.vIntentId;
            // Override intent id only when a valid intentId is present. Get intent will not return intent id
            if (srcCorrelationId) {
              this.config.vIntentId = srcCorrelationId;
            }
            const maskedConsumer = response?.data?.consumer ?? {};

            // constructing consumer object
            Object.assign(data, {
              profiles: [],
              srcCorrelationId
            });
            const profile = {
              idToken: null,
              maskedCards,
              maskedConsumer
            };
            if (!isLocked) {
              SpawnTokenService.spawnForIdToken(this.accessToken).then(spawnResponse => {
                if (spawnResponse.ok) {
                  const issuedTokens = spawnResponse?.data?.issued_tokens ?? null;
                  profile.idToken = getIdToken(issuedTokens);
                  data.profiles.push(profile);
                  message.data = data;
                  logger('getSrcProfile -- success', message);
                  PostMessage.send(message);
                } else {
                  // eslint-disable-next-line no-lonely-if
                  if (this.profileCount > 0) {
                    this.getSrcProfileByIdToken(pId, idTokens, idTokens.length - this.profileCount);
                  } else {
                    // TODO: handle use case last idToken call getting card list > 0, while spawnForIdToken call is failing.
                  }
                }
              });
            } else {
              message.data = data;
              PostMessage.send(message);
            }
          }
        }
      })
      .catch(err => {
        this.profileCount--;
        if (this.profileCount > 0) {
          this.getSrcProfileByIdToken(pId, idTokens, idTokens.length - this.profileCount);
        } else {
          const data = err || {};
          data.pId = pId;
          message.error = data;
          logger('getSrcProfile -- err', err);
          PostMessage.send(message);
        }
      });

    if (!this.gtmFlags.isRecognizedFired) {
      if (!this.accessToken) {
        GtmHelper.trackNonVisaRecognizedUser({
          config: this.config,
          idTokens,
          initData: this.initData
        });
      } else if (!this.gtmFlags.identityLookupFired) {
        GtmHelper.trackNonVisaUnrecognizedReturningUser({
          accessToken: this.accessToken,
          config: this.config,
          idTokens,
          initData: this.initData
        });
      }
    }
  },

  /**
   * Handle multiple id token and fetch card list(getSrcProfile)
   * @param {string} pId
   * @param {array} idTokens
   * @param {number} index
   */
  async getSrcProfileByIdToken(pId, idTokens, index) {
    try {
      await this.generateAccessTokenFromIdToken(idTokens[index]);
      this.getSrcProfile(pId, idTokens);
    } catch (err) {
      this.profileCount--;
      if (this.profileCount > 0) {
        this.getSrcProfileByIdToken(pId, idTokens, idTokens.length - this.profileCount);
      } else {
        // if all generateAccessTokenFromIdToken() call is failed, send error reason to Orch/srci
        const message = { type: EVENTS.SYS_GET_SRC_PROFILE };
        const data = { error: { reason: 'AUTH_INVALID' }, pId };
        message.data = data;
        PostMessage.send(message);
      }
    }
  },

  /**
   * Handle multiple id token and fetch card list(getSrcProfile)
   * @param {string} pId
   * @param {object} srcProfileParam
   */
  getSrcProfileByIdTokens(pId, srcProfileParam) {
    // store otpProvider passed if passed from visa orch
    DcfLaunchData.customProps.transfer.otpProvider = srcProfileParam?.otpProvider ?? '';

    const idTokens = (srcProfileParam && srcProfileParam.idTokens) || [];
    for (const idToken of idTokens) {
      if (!this.idTokens.has(idToken)) {
        this.idTokens.add(idToken);
      }
    }
    const filteredIdTokens = filterTokens(idTokens);
    this.profileCount = (filteredIdTokens && filteredIdTokens.length) || 1;
    // As per design, input IdToken ID token will be ignored when
    // visa access_token is already present.
    // NOTE - This may change when Visa supports multiple identity
    if (
      (isEmptyString(this.accessToken) ||
        !DcfLaunchData.customProps.userInfo.isUserAuthenticated) &&
      Array.isArray(filteredIdTokens) &&
      filteredIdTokens.length
    ) {
      // set start index as first index of idTokens
      this.getSrcProfileByIdToken(pId, filteredIdTokens, 0);
    } else {
      if (this.idTokens.size) {
        AccessTokenService.revokeTokens(this.accessToken, ['id_token'], this.idTokens).then(() => {
          this.idTokens = new Set();
        });
      }
      this.getSrcProfile(pId, filteredIdTokens);
    }
  },

  async getVbaToken() {
    return new Promise(resolve => {
      if (window.VAAP) {
        window.VAAP.getToken(token => resolve(token));
      } else {
        resolve(null);
      }
    });
  },

  /**
   * Go to SRC DCF
   * - Sending post message event to SRC App Shell
   * - Set did based on the screen flow REVIEW
   * @param {string} pId
   * @param {string} did flow name
   */
  goToDcf(pId, did) {
    const message = { type: EVENTS.SYS_GOTO_DCF };
    const cdc = this.config?.cdc ?? false;
    const euIP = this.config?.euIP ?? null;
    const loadDcf = true;
    const params = { cdc, euIP, loadDcf };
    logger('goToDcf -- sending post message back to SRC-I ...', EVENTS.SYS_GOTO_DCF);
    const data = { did, pId, params, status: 'success' };
    message.data = data;
    PostMessage.send(message);
  },

  /**
   * Return to SRC Initiator
   * - Sending post message event back to SRC-I
   * - Set did based on the screen flow CARD_LIST/IDENTITY
   * @param {string} pId
   * @param {string} did flow name
   * @param {string} why event why card-list is requested.
   * @param {object} customData key: accessToken
   */
  async goToInitiator(pId, did, why, customData) {
    const message = {
      data: { did, pId, status: 'success', why },
      type: EVENTS.SYS_GOTO_INITIATOR
    };

    logger('goToInitiator -- sending post message back to SRC-I ...', EVENTS.SYS_GOTO_INITIATOR);
    this.config.isDCF = false; //set to false when navigate back to SRC-I
    if (did === CARD_LIST) {
      this.config.showCardList = true; //set to true when card list screen needs to be displayed
    }

    // If the DCF passes in an x-auth-code, we need to generate an access token out of it
    if (customData && customData.xAuthCode) {
      try {
        const response = await AccessTokenService.generateAccessToken(
          customData.xAuthCode,
          this.sessionId,
          { eventSubType: 'ContinueSession' }
        );
        if (response.ok) {
          this.updateAccessToken(response);
          this.setProfilePreferences(response);
        } else {
          // What happens if we failed to generate an access token???
          // Should we retry the API? We're gonna need a valid token when entering SRCi.
        }
      } catch (err) {
        const data = err || {};
        data.pId = pId;
        message.error = data;
      }
    }

    PostMessage.send(message);
  },

  handleAcceptNewTermsResponse(response, isB2CIAM) {
    DcfLaunchData.setUserInfo({
      isTncFlow: true,
      tncStatus: NOT_ACCEPTED
    });
    let tncEntryInfo;
    if (isB2CIAM) {
      tncEntryInfo = response.data?.data?.terms?.tncEntryInfo;
    } else {
      tncEntryInfo = response.data?.tncEntryInfo;
    }
    DcfLaunchData.setTokens({ tncEntryInfo });
  },

  handleResponse(data) {
    const message = {};
    const { baseRequestPayload, dataLayer, gtmData, intentId, network, response } = data;

    if (gtmData) {
      gtmData.correlation_id = StaticIdSingletonClass.correlationId;
    }

    if (response.dcfActionCode === 'CANCEL') {
      message.type = 'cancel';
      message.data = { dataLayer };
      PostMessage.send(message);
      this.resetUserConfig();
      this.accessToken = this.expiresIn = undefined;
    } else {
      PaymentDataService.paymentData(
        response,
        network,
        intentId,
        baseRequestPayload,
        this.config.apikey
      ).then(response => {
        let messageData = {};
        if (response.ok) {
          message.type = 'success';
          const toMerchantData = getDataToMerchant(response.data);
          messageData = toMerchantData;
        } else {
          message.type = 'cancel';
          messageData.error = response;
        }
        messageData.dataLayer = dataLayer;
        message.data = messageData;
        PostMessage.send(message);
      });
      this.resetUserConfig();
      // VCO-14889 SSI user - Uncheck remember me on MC DCF - User doesn't sign out
      // When non-visa sdk unbindAppInstance flag is true, accessToken cannot be removed directly,
      // it will be used to revocate remember_me and accessToken. AccessToken will be reset after
      // getting revocation api response
      if (response.unbindAppInstance === false) {
        this.accessToken = this.expiresIn = undefined;
      }
    }
  },

  /**
   * Handle DCF update/purchase intent response
   * - Sending response back to SRC-I
   * @param {object} data
   */
  handleUpdateIntent(data) {
    if (data.response) {
      data.response.callid = data.response.callid || this.config.vIntentId;
    }

    const message = { type: EVENTS.SYS_DCF_SUCCESS };
    logger('handleUpdateIntent -- sending post message back to SRC-I ...');
    message.data = data;
    PostMessage.send(message);
  },

  /**
   * 3.4 Check if user is recognized.
   * @param {string} [data.branding] SRC or VCO
   * @param {object} identityLookUpParams
   * @param {string} pId
   */
  async identityLookup({ branding, identityLookUpParams, pId }) {
    const message = { type: EVENTS.SYS_IDENTITY_LOOKUP };
    //Validate Init data
    const error = validateIdLookupData(identityLookUpParams.consumerIdentity);
    if (!error) {
      this.gtmFlags.identityLookupFired = true;
      const lookupGtmData = {
        apiName: 'idLookup',
        email: identityLookUpParams.consumerIdentity.identityValue,
        event: 'user look up request',
        lookupStatus: 'undefined',
        saveMyInfoWithVisa: 'undefined',
        userFlow: 'existing'
      };
      PostMessage.send({ data: lookupGtmData, type: EVENTS.SYS_SEND_USERNAME_LOOKUP_GTM_DATA });

      IdLookupService.idLookup({
        apikey: this.config.apikey,
        branding,
        email: identityLookUpParams.consumerIdentity.identityValue,
        sessionId: this.sessionId,
        thmProfileHint: this.config.thmProfileHint,
        usernameType: identityLookUpParams.consumerIdentity.usernameType
      })
        .then(response => {
          logger('idLookup response', response);
          const messageData = { pId };
          const lockOutFlow = getLockOutFlowForB2CIAMApis(response);
          const { data: responseBody } = response;
          let lookupStatus;
          if (responseBody) {
            lookupStatus =
              typeof responseBody.data?.actions === UNDEFINED
                ? PRESENT
                : responseBody.data?.actions?.[0] === B2CIAM_ENUMS.ID_LOOKUP_ENROLL
                  ? NOTPRESENT
                  : USERNOTALLOWED;
          }
          const userInfo = {
            email: identityLookUpParams.consumerIdentity.identityValue,
            isLocked: Boolean(lockOutFlow)
          };
          if (userInfo.isLocked) {
            this.launchThm(true);
          }
          if (lookupStatus) {
            userInfo.lookupStatus = lookupStatus;
            const lookupGtmData = {
              apiName: 'idLookup',
              email: identityLookUpParams.consumerIdentity.identityValue,
              event: 'user look up response',
              lookupStatus,
              saveMyInfoWithVisa: lookupStatus === NOTPRESENT ? 'true' : 'false',
              userFlow:
                lookupStatus === NOTPRESENT ? GtmHelper.USER_FLOW.NEW : GtmHelper.USER_FLOW.EXISTING
            };
            PostMessage.send({
              data: lookupGtmData,
              type: EVENTS.SYS_SEND_USERNAME_LOOKUP_GTM_DATA
            });
          }
          DcfLaunchData.setUserInfo(userInfo);
          if (!response.ok || lookupStatus === USERNOTALLOWED) {
            const lookupError = ConvertApiErrorClass.handleIdentityLookupError(response);
            messageData.error = lookupError;

            if (lookupStatus === USERNOTALLOWED) {
              LocalStorageUtil.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);
            }
          } else {
            this.setProfilePreferences(response, true);
            if (StaticIdSingletonClass.visaSrci && !StaticIdSingletonClass.isVsb) {
              if (this.config.profileLocale && this.config.profileCountry) {
                messageData.customData = {
                  data: {
                    country: this.config.profileCountry,
                    locale: this.config.profileLocale
                  }
                };
              }
            }
            const isNewUser = lookupStatus === NOTPRESENT;
            this.userId = identityLookUpParams.consumerIdentity.identityValue;
            this.userIdType = identityLookUpParams.consumerIdentity.type || DEFAULT_USER_TYPE;
            this.usernameType = identityLookUpParams.consumerIdentity.usernameType;
            messageData.consumerPresent = lookupStatus === PRESENT;
            messageData.lastUsedCardTimestamp = response?.data?.data?.lastUsedCardTimestamp;
            if (StaticIdSingletonClass.visaSrci) {
              messageData.customData = response;
            }
            if (messageData.consumerPresent) {
              this.updateAccessToken(response, false);
              LocalStorageUtil.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);
            }
            // TODO: validate if X_USER_CRED is still available in idLookup api
            const xUserCred =
              response.headers[X_USER_CRED.toLowerCase()] || response.headers[X_USER_CRED];
            const isPasswordlessUser = (xUserCred && xUserCred.toUpperCase() === NOTEXIST) || false;
            if (StaticIdSingletonClass.visaSrci) {
              messageData.isPasswordlessUser = isPasswordlessUser;
            } else {
              const gtmData = {
                apikey: this.config.apikey,
                guest_vco_account: lookupStatus || UNDEFINED,
                save_my_info_with_visa: isNewUser ? 'true' : 'false',
                user_flow: isNewUser ? GtmHelper.USER_FLOW.NEW : GtmHelper.USER_FLOW.EXISTING
              };
              if (this.dpaData) {
                gtmData.src_dpa_id = this.dpaData.srcDpaId;
              }
              Object.assign(this.nonVisaGtmCustomAttributes, gtmData);
              GtmHelper.pushGtmData(gtmData);
            }
            DcfLaunchData.setUserInfo({
              isPasswordlessUser
            });
          }
          message.data = messageData;
          PostMessage.send(message);
        })
        .catch(err => {
          const data = { pId };
          data.error = err;
          logger('idLookup -- err', err);
          message.data = data;
          PostMessage.send(message);
        });
    } else {
      const data = { pId };
      data.error = error;
      logger('identityLookup error -- error', error);
      message.data = data;
      PostMessage.send(message);
    }
  },

  /**
   * 3.1 Initialize SRC System SDK
   *
   * For more information please see `/docs/SYSTEM_SDK_EXTERNAL.md`
   *
   * @param {object} initData
   * @param {string} pId
   * void @return
   */
  init(initData, pId) {
    const message = { data: { pId }, type: EVENTS.SYS_INIT };
    const {
      debug, // to enable console logs: boolean default as false,
      isProduction,
      visaSrci // visa srci parameter: boolean default as false,
    } = initData;
    const namespace = getNamespace();
    let error = null;
    this.config = {};
    this.config.userInfo = {};
    this.dpaData = initData && initData.dpaData;
    this.gtmFlags = {
      identityLookupFired: false,
      isRecognizedFired: false
    };
    this.idTokens = new Set();
    this.initData = initData;
    this.revokedIdTokens = new Set();
    this.__maskedCards = [];

    let gtmData;
    if (!visaSrci) {
      error = this.setExternalSrciInputData(initData);
      this.setStaticIds(namespace, visaSrci, debug, isProduction);
      // VCO-11068: Track GTM custom attributes for non-Visa SRCi as data layer carried over from SRCi not available
      Object.assign(
        this.nonVisaGtmCustomAttributes,
        GtmHelper.trackNonVisaSRCiCustomAttributes(this.config)
      );

      gtmData = {
        correlation_id: this.config.correlationId,
        xo_visitid: this.config.xo_visitid
      };
      Object.assign(this.nonVisaGtmCustomAttributes, gtmData);
    } else {
      error = this.setVisaSrciInputData(initData);
      this.setStaticIds(namespace, visaSrci, debug, isProduction);
      gtmData = {
        correlation_id: this.config.correlationId
      };
    }
    // Common for both Visa/Non-Visa SRC-I
    collapsedlogger('init complete -- apiConfig', this.config);
    if (error) {
      message.data.error = error;
      collapsedlogger('init error -- error', error);
    }
    PostMessage.send(message);
    GtmHelper.trackTransactionAndOrderType(this.config.merchantConfig);
    GtmHelper.pushGtmData(gtmData);
  },

  /**
   *  3.5 Generate and send otp to user's email
   *  - prior calling generate otp api, we would require access token
   *  - initiateIdentityValidation can be invoked recurringly (re-send otp feature)
   *    in this instance, if accessToken is not null or authCode is updated,
   *    system sdk should invoke only sendOtp
   * @param {string} pId
   * void @return
   */
  initiateIdentityValidation(pId) {
    const message = { type: EVENTS.SYS_INITIATE_IDENTITY_VALIDATION };
    // if accessToken is empty, invoke generateAccessToken else just call sendOtp
    if (isEmptyString(this.accessToken)) {
      message.error = { reason: AUTH_ERROR };
      PostMessage.send(message);
    } else {
      Adapter.reissuance(pId, message);
    }
  },

  /**
   * instantiateCheckoutApp
   *
   * Instantiate the app to show the loader screen. This is done to prevent popup blocker in popup use cases.
   *
   * @param {object} data
   */
  instantiateCheckoutApp({ dataLayer, externalProfileId, isSRCBranded, pId }) {
    /**
     * In external SRCi flows we don't want to show the system-sdk loader screen and
     * instead use the loader that lives within the app
     */
    const message = getCheckoutMessage({
      apikey: this.config.apikey,
      dataLayer,
      dcfDomain: this.getDcfDomain(),
      dpaId: this?.srciDpaId || this?.dpaData?.srcDpaId || '',
      externalProfileId,
      isSRCBranded,
      isSplunkLoggingEnabled: SplunkLoggingSingleton.isEnabled,
      launchAppForLoader: true,
      pId
    });
    PostMessage.send(message);
    logger('Checkout -- launching app to hold on loader until ready for routing ...', message);
  },

  /**
   *  Check if user is recognized using the new /recognize api
   *
   *  Used if `VISA_GLOBALS.enabledFeatureToggles` object includes `recognizeApi`.
   *  This api precludes the need to call /spawn because the api handles that.
   *
   *  - if response contains secured_id_token hint, remove ssiEnabled cookie.
   *  - update the access_token received as part of the SidToken Request in sdk cache.
   *
   *  - if response contains activity_ref hint, remove ermEnabledCookie cookie.
   *
   * @param {object} data
   * @param {string} data.pId
   * @param {string} [data.branding] SRC or VCO
   */
  isRecognized(data) {
    mark(marks.isRecognizedStart);

    RecognizeService.recognize({
      apikey: this.config.apikey,
      branding: data.branding,
      sessionId: this.sessionId
    })
      .then(response => {
        const message = { type: EVENTS.SYS_IS_RECOGNIZED };
        const lockOutFlow = getLockOutFlowForB2CIAMApis(response);
        const isLocked = Boolean(lockOutFlow);
        const lockOutError = { reason: ACCT_INACCESSIBLE };
        const dataForRecognizedUserResponse = {
          isRecognizedResponse: response,
          isVsb: this.config.isVsb,
          message,
          pId: data.pId
        };
        // F21304 - IAM previously returned 400 for user without ERM or SSI serverside cookies;
        //  it now returns 204 with empty response, but we should handle this the same as the 400
        const isNoCookiesRecognizeResponse = response.ok && isEmptyObject(response.data);

        if (!response.ok || isNoCookiesRecognizeResponse) {
          this.gtmFlags.isRecognizedFired = true;
          // VCO-14406 - fire this tracking event in /recognize flow too
          DcfLaunchData.setUserInfo({
            isLocked
          });
          // pass empty error object when not locked so `sendRecognizedUserResponse` will set `data.recognized` to false
          dataForRecognizedUserResponse.error = isLocked ? lockOutError : {};
          sendRecognizedUserResponse(dataForRecognizedUserResponse);
          GtmHelper.trackVisaUnrecognizedReturningUser({
            accessToken: '',
            apiName: 'isRecognized',
            config: this.config,
            idTokens: [],
            initData: this.initData
          });
        } else {
          dataForRecognizedUserResponse.isB2CIAMRecognized = true;
          logger('recognize success response', response);
          const authStatus = response.data?.auth_status;
          const issuedTokens = response.data?.issued_tokens || [];
          const isTNC = authStatus === B2CIAM_ENUMS.RECOGNIZED_PARTIAL;
          let flowType = FLOW_TYPE.STD;
          for (const issuedToken of issuedTokens) {
            if (issuedToken.token_type_hint.includes(B2CIAM_ENUMS.ACTIVITY_REF_SSI)) {
              flowType = FLOW_TYPE.SSI;
            } else if (issuedToken.token_type_hint.includes(B2CIAM_ENUMS.ACTIVITY_REF_ERM)) {
              flowType = FLOW_TYPE.ERM;
              setErmEnabled(); // serverside activity_ref cookie set by /recognize, so update ermEnabled clientside cookie with no value for auth-ref
            }
          }
          this.setUserInfoForRecognized();
          this.updateAccessToken(response);
          this.setProfilePreferences(response, true);
          this.userId = getUsername();
          this.userIdType = DEFAULT_USER_TYPE;
          const idToken = getIdToken(issuedTokens);
          if (authStatus === B2CIAM_ENUMS.RECOGNIZED_FULL) {
            // SSI/ERM user
            if (flowType === FLOW_TYPE.SSI) {
              this.config.isSSICookieRefreshed = true;
            } else if (flowType === FLOW_TYPE.ERM) {
              this.config.isERMCookieRefreshed = true;
            }
          } else if (isTNC) {
            // TNC user
            this.config.isSSICookieRefreshed = false;
            this.config.isERMCookieRefreshed = false;
            this.handleAcceptNewTermsResponse(response, true);
          }
          this.gtmFlags.isRecognizedFired = true;
          // VCO-14406 - fire this tracking event in /recognize flow too
          DcfLaunchData.setUserInfo({
            // TODO: VCO-14814: replace with actual flowType after BE makes the fix for TNC use case flow
            flowType,
            isRecognizedUser: true,
            isUserAuthenticated: true
          });
          dataForRecognizedUserResponse.accessTokenExpiresIn = this.expiresIn;
          sendRecognizedUserResponse(dataForRecognizedUserResponse);
          GtmHelper.trackVisaRecognizedUser({
            accessToken: idToken,
            config: this.config,
            initData: this.initData,
            user_flow: GtmHelper.USER_FLOW.EXISTING
          });
        }
      })
      .catch(err => {
        logger('recognize error response', err);
      });
  },

  /**
   * Initialize SRC DCF
   * @param {object} config
   * @param {string} pId
   */
  launchDcf() {
    // TODO Create DCF iFrame
    const iFrameWrapper = VCOPUtils.createIFrame(
      SRC_DCF_CONSTANTS.IFRAME_NAME,
      SRC_DCF_CONSTANTS.IFRAME_WRAPPER_ID,
      SRC_DCF_CONSTANTS.IFRAME_ID,
      () => {
        const iframe = document.getElementById(SRC_DCF_CONSTANTS.IFRAME_ID);
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(`app:render--${JSON.stringify(Date.now())}`);
        }
      }
    );
    iFrameWrapper.appendChild(VCOPUtils.createIFrameStyle(SRC_DCF_CONSTANTS.IFRAME_ID));
    const iFrame = iFrameWrapper.firstChild;
    const loadDCF = '&loadDcf=true';
    iFrame.src = this.domain + loadDCF;

    if (!document.getElementById(SRC_DCF_CONSTANTS.IFRAME_WRAPPER_ID)) {
      document.body.appendChild(iFrameWrapper);
    }
    iFrame.focus();
  },

  /**
   * Launch/relaunch thm iframe
   * launch thm requires sessionId passed by init()
   * reLaunch thm requires newly generated sessionId
   * @param {boolean} isReLaunch
   */
  launchThm(isReLaunch = true) {
    thmProfilingComplete = false;
    Adapter.config.thmProfileHint = null;

    if (isReLaunch) {
      this.sessionId = systemSessionId.create();
      StaticIdSingletonClass.sessionId = this.sessionId;
    }
    if (StaticIdSingletonClass.visaSrci && !this.config.isVsbButtonless) {
      const message = {
        data: {
          sessionId: this.sessionId
        },
        type: 'launchThmIframe'
      };
      PostMessage.send(message);
    } else {
      const dataObj = {
        correlationId: this.config.correlationId,
        namespace: StaticIdSingletonClass.namespace,
        orgId: window?.VISA_GLOBALS?.orgId ?? THM_ORG_ID.PROD,
        sessionId: this.sessionId
      };
      initiateTHM(dataObj, PostMessage);
    }
  },

  nonVisaGtmCustomAttributes: {},

  probe(data, pId) {
    const message = { type: EVENTS.SYS_COMPLETE_IDENTITY_VALIDATION };
    OTPB2CIAMService.probe(data.validationData, this.accessToken, this.sessionId)
      .then(response => {
        const messageData = { pId };
        if (!response.ok) {
          const errorString = response?.errorObject?.data?.result?.status || UNKNOWN_ERROR;
          // errorString enums: [ACCT_INACCESSIBLE, CODE_INVALID, CODE_EXPIRED, UNKNOWN_ERROR]
          // If user is locked, clear token so new token will be generated when necessary
          if (errorString === ACCT_INACCESSIBLE) {
            this.accessToken = null;
            DcfLaunchData.setUserInfo({
              isLocked: true
            });
          }
          messageData.error = { reason: errorString };
          message.data = messageData;
          PostMessage.send(message);
        } else {
          DcfLaunchData.setUserInfo({ isLoggedInOtpUser: true, isUserAuthenticated: true });
          this.accessToken = response.data.access_token;
          this.expiresIn = response.data.expires_in;
          this.isFullAccessToken = true;
          DcfLaunchData.setTokens({ expiresIn: this.expiresIn });
          this.startSession(this.expiresIn);
          this.setProfilePreferences(response, true);
          const authStatus = response.data?.auth_status;
          const isTNC = authStatus === B2CIAM_ENUMS.RECOGNIZED_PARTIAL;
          if (authStatus === B2CIAM_ENUMS.RECOGNIZED_FULL || isTNC) {
            // Update DcfLaunchData for pending TnC users.
            // Invoke campign service for logged in users.
            if (isTNC) {
              this.handleAcceptNewTermsResponse(response, true);
            }

            /**
             * For psp when isRecognized is true but user does otp login with different email,
             * make them not recognized
             */
            if (getUsername() && this.userId !== getUsername()) {
              this.unbindAppInstance({}, true);
              DcfLaunchData.setUserInfo({ isRecognizedUser: false });
            }

            this.resetErmActivityToken();
            this.resetSsiSecuredIdToken();
            const issuedTokens = response?.data?.issued_tokens ?? null;
            messageData.idToken = getIdToken(issuedTokens);
            if (this.initData.visaSrci) {
              messageData[OTP_SUCCESS_VALIDATION_CHANNEL] =
                response.headers?.['X-DELIVERY-ADDRESS-TYPE'];
            }
            message.data = messageData;
            PostMessage.send(message);
          }
        }
      })
      .catch(err => {
        const messageData = err || {};
        messageData.pId = pId;
        message.error = messageData;
        logger('completeIdentityValidation -- err');
        PostMessage.send(message);
      });
  },
  /**
   * processDpaTransactionOptionsEmptyObj
   *
   * throw error when dpaTransactionOptions exists and not an {obnject}
   *
   * @param {object} data
   */
  processDpaTransactionOptionsEmptyObj({ dataLayer, pId }) {
    let message = getCheckoutMessage({
      dataLayer,
      error: getCheckoutError(CHECKOUT_ERRORS.DPA_TRANSACTION_OPTIONS),
      pId
    });
    PostMessage.send(message);
    // Do not close the app when it is visa-srci on error
    if (!StaticIdSingletonClass.visaSrci) {
      message = getDismissAppMessage();
      PostMessage.send(message);
    }

    logger('Checkout -- sending post message back to Visa SDK ...', message);
  },

  pushEvents(data) {
    try {
      const externalClientId = getExternalClientId(this.config);
      const clientId = getClientId(this.config);
      PushEventsService.eventLogging(
        {
          ME: clientId,
          XCL: externalClientId,
          'x-merchant-api-key': this.config.apikey,
          ...data
        },
        this.config.correlationId
      );
    } catch (e) {
      collapsedlogger('push events failed', e);
    }
  },

  /**
   * Update multiple-vInit configs inside system-sdk
   * @param {object} data
   */
  async pushUpdatedVinit(orchCustomData) {
    //TODO: update dpaTransactionOptions for VISA in phase 2
    const vInitRequest = this.config.vInitRequest;
    const updatedVInitRequest = orchCustomData?.data ?? vInitRequest;
    this.config.vInitRequest = Object.assign({}, updatedVInitRequest);
    this.config.isVsb = orchCustomData.data?.isVsb ?? false;
    this.config.isVsbButtonless = orchCustomData.data?.isVsbButtonless ?? false;
  },
  /**
   * Send otp to the user's email id with new B2C IAM reissuance api
   * @param {string} pId
   * @param {object} message
   */
  reissuance(pId, message = {}) {
    OTPB2CIAMService.reissuance(this.accessToken, this.sessionId)
      .then(response => {
        const data = { pId };
        const maskedValidationChannel = response.data?.data?.maskedValidationChannel;
        if (!response.ok || isEmptyObject(maskedValidationChannel)) {
          // errorString enums: [ ACCT_INACCESSIBLE, RETRIES_EXCEEDED, UNKNOWN_ERROR ]
          let errorString =
            response?.errorObject?.data?.result?.status || response.data?.data?.result?.status;
          if (!errorString || errorString === UNKNOWN_ERROR) {
            errorString = OTP_SEND_FAILED;
          }
          if (errorString === ACCT_INACCESSIBLE) {
            this.accessToken = this.expiresIn = undefined;
          }
          data.error = { reason: errorString };
        } else {
          data.maskedValidationChannel = maskedValidationChannel?.maskedValidationChannel;
          logger('initiateIdentityValidation -- success');
          this.accessToken = response.data.access_token;
          this.expiresIn = response.data.expires_in;
          DcfLaunchData.setTokens({ expiresIn: this.expiresIn });
          this.startSession(this.expiresIn);
        }
        data.profileLocale = this.config.profileLocale;
        data.profileCountry = this.config.profileCountry;
        message.data = data;
        PostMessage.send(message);
      })
      .catch(err => {
        const data = err || {};
        data.pId = pId;
        message.error = data;
        logger('initiateIdentityValidation -- err');
        PostMessage.send(message);
      });
  },
  /**
   * Renews an access token which is about to expire
   * @param {string} pId
   */
  renewSession(pId) {
    const message = { data: { pId }, type: EVENTS.SYS_SESSION_RENEW };

    AccessTokenService.renewSession(this.accessToken, this.sessionId)
      .then(response => {
        if (response.ok) {
          /**
            * Don't use `this.updateAccessToken` here b/c the app handles updating its sessions differently
            when renewing an access token.
            * Normally the system-sdk will initiate the conversation with the SRCi that it has a new access
            token and the SRCi should restart its session.
            * But this use case is unique b/c the SRCi is actually reaching out to the system-sdk first, alerting
            it that the its token needs to be renewed.
            * For session renewals the app is listening for EVENTS.SYS_SESSION_RENEW. All other cases the app
            is listening to EVENTS.SYS_SESSION_START.
            */
          this.accessToken = response?.data?.access_token;
          message.data = { ...message.data, ...response };
        }

        PostMessage.send(message);
      })
      .catch(err => {
        const data = err || {};
        data.pId = pId;
        message.error = data;
        PostMessage.send(message);
      });
  },

  resetDCFWhy() {
    delete this.config.why;
  },

  /**
   * Reset Spawn API for ERM
   *  - To reset activity server side cookie and authRef in ermEnabled cookie
   *    when activity token api fails
   *  - Validate username cookie & erm cookie username are same. if not delete the cookie
   *  - Validate userId received via idlookup & ssi cookie username are same. if not delete the cookie
   */
  resetErmActivityToken() {
    if (isErmEnabled()) {
      const userNameFromErmCookie = getUsernameFromErmCookie();
      const isValidUser = this.userId === userNameFromErmCookie;
      if (isValidUser) {
        SpawnTokenService.spawnForErm(this.accessToken).then(spawnResponse => {
          const issuedToken = spawnResponse?.data?.issued_tokens ?? null;
          setErmActivityToken(issuedToken);
        });
      } else {
        deleteSsiErmCookies();
      }
    }
  },

  /**
   * Reset Spawn API for SSI
   *  - To reset secured_id_token server cookie when sidToken api fails
   *  - Validate username cookie & ssi cookie username are same. if not delete the cookie
   *  - Validate userId received via idlookup & ssi cookie username are same. if not delete the cookie
   */
  resetSsiSecuredIdToken() {
    if (hasValidStaySignedIn()) {
      const userNameFromSsiCookie = getUsernameFromSsiCookie();
      const isValidUser = this.userId === userNameFromSsiCookie;
      if (isValidUser) {
        SpawnTokenService.spawnForSsi(this.accessToken).then(spawnResponse => {
          if (spawnResponse.ok) {
            this.config.isSSICookieRefreshed = true;
          }
        });
      } else {
        deleteSsiErmCookies();
      }
    }
  },

  /**
   * Reset the user related config information
   *  - Invoked during Reset functionality
   */
  resetUserConfig() {
    Object.assign(this.config, {
      selectedCardId: null,
      showCardList: false,
      showLocales: true,
      userInfo: { isRecognizedUser: false, ssiErmFlow: null },
      vIntentId: null
    });
    DcfLaunchData.reset();
  },

  revokeIdTokens() {
    const promiseArr = [];
    for (const idToken of this.idTokens) {
      if (!this.revokedIdTokens.has(idToken)) {
        this.revokedIdTokens.add(idToken);
        const idTokenPromise = new Promise(resolve => {
          AccessTokenService.revokeToken(this.accessToken, 'id_token', idToken).then(res => {
            resolve({ res, type: 'id_token' });
          });
        });
        promiseArr.push(idTokenPromise);
      }
    }
    return promiseArr;
  },
  /**
   * routeToDcfForSelectCard
   *
   * Route BAU select card user in DCF to RnC.
   *
   * @param {object} data
   */
  routeToDcfForSelectCard({ isCardFirstFlow, srcDigitalCardId }) {
    let message;
    const toRoute = REVIEW;
    const LOGGER_TEXT =
      'Checkout -- sending post message back to Visa SDK to route user in DCF ...';
    /*
     * F27004 if srcDigitalCardId is in payment instrument map, it is a vPanEnrollmentId/digitalCardId.
     * Map it back to payment instrument id before sending to DCF
     */
    Object.assign(this.config, {
      isCardFirstFlow,
      selectedCardId: this.piMapping?.[srcDigitalCardId] ?? srcDigitalCardId,
      toRoute
    });
    isConfigsReadyForDcf = true;
    if (didDcfAskForConfigs) {
      message = getRouteUserInDCFMessage();
      PostMessage.send(message);
    }
    logger(LOGGER_TEXT, message);
  },

  /**
   * This functionality is not in INO JS API spec.
   *  - This is a generic adapter function used to handle session timeout
   *  - fn can be invoked by DCF/SRC-I
   * @param {string} pId
   * @param {string} why
   */
  sessionTimeout(pId, why) {
    this.resetUserConfig();
    this.accessToken = this.expiresIn = undefined;
    if (why === DCF_TIMEOUT_RESET) {
      const data = { pId };
      PostMessage.send({ data, type: EVENTS.SYS_SESSION_TIMEOUT });
    }
  },

  /**
   * set cookie country
   * @param {object} data
   * @return {object} updated data
   */
  setCookieCountry(data) {
    if (getCookie('country_preference') && getCookie('locale')) {
      data.cookieCountry = getCookie('country_preference');
      data.cookieLocale = getCookie('locale');
    } else if (data && data.countryCode) {
      data.cookieCountry = data.countryCode;
      data.cookieLocale = data.locale;
    }
    return data;
  },

  /**
   * Set DpaTransactionOption to system sdk config
   * @param {object} dpaTransactionOptions
   */
  setDpaTransactionOptions(dpaTransactionOptions) {
    if (!isEmptyObject(dpaTransactionOptions)) {
      // TODO: needs to be moved to map-merchant-config helper function post M3.
      const payment = {};
      //Keep copy of dpaTransactionOptions in config object, needed for enrollCard
      this.config.dpaTransactionOptions = dpaTransactionOptions;
      const acceptedBillingCountries = dpaTransactionOptions.dpaAcceptedBillingCountries;
      if (acceptedBillingCountries && acceptedBillingCountries.length) {
        payment.billingCountries = acceptedBillingCountries;
      }
      const collectBillingAddress = this.config?.merchantConfig?.billingConstraints
        ?.collectBillingAddress;
      payment.collectBillingAddress = collectBillingAddress;
      const acceptedShippingCountries = dpaTransactionOptions.dpaAcceptedShippingCountries;
      const shippingConstraints = {
        collectShipping: this.config?.merchantConfig?.shippingConstraints?.collectShipping
      };
      if (acceptedShippingCountries && acceptedShippingCountries.length) {
        shippingConstraints.acceptedRegions = acceptedShippingCountries;
      }
      this.config.locale = dpaTransactionOptions.dpaLocale;
      const buttonAction =
        dpaTransactionOptions.reviewAction &&
        dpaTransactionOptions.reviewAction.toUpperCase() === BUTTON_ACTION.PAY
          ? BUTTON_ACTION.PAY
          : DEFAULT_VALUE.BUTTON_ACTION;

      if (isEmptyString(this.config.orderId)) {
        delete this.config.orderId;
      }

      this.config.countryCode = dpaTransactionOptions.merchantCountryCode;
      if (isEmptyString(this.config.countryCode)) {
        delete this.config.countryCode;
      }
      this.config.reviewMessage = dpaTransactionOptions.checkoutDescription;
      let total;
      let currencyCode;
      if (!isEmptyObject(dpaTransactionOptions.transactionAmount)) {
        total = dpaTransactionOptions.transactionAmount.transactionAmount;
        currencyCode = dpaTransactionOptions.transactionAmount.transactionCurrencyCode;
      }

      /**
        * VCO-14146 - set payMode (true or false). Will be added to DcfLaunchData and then automatically
        added to redux state when app loads and calls fetchConfigs
        */
      this.config.payMode =
        (!isEmptyString(dpaTransactionOptions.reviewAction) &&
          dpaTransactionOptions.reviewAction.toUpperCase() === BUTTON_ACTION.PAY) ||
        false;

      if (
        dpaTransactionOptions?.customInputData?.customFlowType?.toLowerCase() === PAYMENT_SETTING
      ) {
        this.config.customFlowType = PAYMENT_SETTING;
      } else if (
        dpaTransactionOptions?.customInputData?.customFlowType?.toLowerCase() === WITHIN_CHECKOUT
      ) {
        this.config.customFlowType = WITHIN_CHECKOUT;
      } else {
        this.config.customFlowType = null;
      }

      this.config.vInitRequest = {
        ...this.config.merchantConfig,
        intialVinit: {},
        payment,
        paymentRequest: {
          buttonAction,
          currencyCode,
          orderId: dpaTransactionOptions.merchantOrderId,
          total
        },
        shipping: shippingConstraints
      };
      delete this.config.vInitRequest.billingConstraints;
      delete this.config.vInitRequest.shippingConstraints;
    } else {
      this.config.vInitRequest = {
        ...this.config.merchantConfig,
        intialVinit: {}
      };
    }
  },

  /**
   * Set Input initData to the config
   * @param {object} initData
   */
  setExternalSrciInputData(initData) {
    //Validate Init data
    const error = validateInitData(initData);
    if (!error) {
      this.setIsSplunkLoggingEnabled(initData);
      logButtonClick(initData);
      const {
        dpaData,
        dpaTransactionOptions,
        srcInitiatorId,
        srciDomain,
        srciDpaId,
        srciTransactionId,
        xo_visitid
      } = initData;
      this.sessionId = systemSessionId.create();
      this.config.sessionId = this.sessionId;
      this.domain = srciDomain;
      this.config.correlationId = srciTransactionId;

      const canUseStorage = canUseSessionStorage();
      if (canUseStorage && sessionStorage.getItem('visitId')) {
        this.config.xo_visitid = sessionStorage.getItem('visitId');
      } else {
        this.config.xo_visitid = xo_visitid;

        if (canUseStorage) {
          sessionStorage.setItem('visitId', xo_visitid);
        }
      }
      this.config.visitId = this.config.xo_visitid;
      PostMessage.send({
        data: { xo_visitId: this.config.xo_visitid },
        type: EVENTS.SYS_UPDATE_VISIT_ID
      });

      this.config.apikey = srcInitiatorId;
      this.config.consumerNationalIdentifierRequested =
        dpaTransactionOptions?.consumerNationalIdentifierRequested || false;
      this.config.merchantConfig = mapMerchantConfig(dpaData, dpaTransactionOptions);
      if (srciDpaId) {
        this.config.merchantConfig.externalClientId = srciDpaId;
        this.srciDpaId = srciDpaId;
      }
      this.config.eventSource = SRC_SYSTEM.DEFAULT_EVENT_SOURCE;
      this.config.channel = SRC_SYSTEM.DEFAULT_CHANNEL;
      this.setDpaTransactionOptions(dpaTransactionOptions);
      this.launchThm(false);
    }
    return error;
  },

  /**
   * Check if the config data has a flag to determine if splunk logging should be enabled for this user journey.
   * If the flag doesn't exist, then the system-sdk needs to generate this flag and its value.
   * @param {object} initData
   */
  setIsSplunkLoggingEnabled(initData) {
    /**
     * Set if we're running in cypress mode. If we are we want to override the `isEnabled` property
     * and disable logEvent api calls b/c the flood the network tab and console.
     * `initData.isCypressMode` is set in sdk-test.ejs & `initData.data.isCypressMode` comes from sdk2/config/index.ts
     */
    if (initData?.isCypressMode || initData?.data?.isCypressMode) {
      SplunkLoggingSingleton.isCypressMode = true;
    }

    // If configs already have been set then use that value for the singleton
    if (this.config.hasOwnProperty('isSplunkLoggingEnabled')) {
      SplunkLoggingSingleton.isEnabled = this.config.isSplunkLoggingEnabled;
      return;
    }

    // check if the initData has the flag for splunkLogging
    if (initData?.data?.hasOwnProperty('isSplunkLoggingEnabled')) {
      SplunkLoggingSingleton.isEnabled = initData.data.isSplunkLoggingEnabled;
      this.config.isSplunkLoggingEnabled = SplunkLoggingSingleton.isEnabled;
      return;
    }

    // If neither initData or configs got the flag then the system-sdk must generate that flags value
    SplunkLoggingSingleton.isEnabled = getIsSplunkLoggingEnabled();
    this.config.isSplunkLoggingEnabled = SplunkLoggingSingleton.isEnabled;
  },

  /**
   * Set Profile locale/country preference based on the login API response
   *  - Profile country will always be iso_country for eg. Country code UA has 804 as the iso country code
   *  - Set country_preference/locale cookies
   *  - Update this.config with profileCountry & profileLocale
   * @param {object} response
   * @param {boolean} isB2CIAM
   */
  setProfilePreferences(response, isB2CIAM = false) {
    let profileCountry;
    let profileLocale;
    if (isB2CIAM) {
      profileCountry = response.data?.data?.locality?.country ?? null;
      profileLocale = response.data?.data?.locality?.locale ?? null;
    } else {
      profileCountry = response.data?.country ?? null;
      profileLocale = response.data?.locale ?? null;
    }

    if (profileLocale && profileCountry) {
      Object.assign(this.config, { profileCountry, profileLocale });
      if (DcfLaunchData) {
        DcfLaunchData.customProps.profileCountry = profileCountry;
        DcfLaunchData.customProps.profileLocale = profileLocale;
        DcfLaunchData.setUserInfo({
          countryCode: getCountryCodeByISO(profileCountry, getSupportedLocales()),
          locale: profileLocale
        });
      }
    }
  },

  setStaticIds(namespace, visaSrci = false, debug = false, isProduction = false) {
    StaticIdSingletonClass.namespace = namespace;
    StaticIdSingletonClass.sessionId = this.config.sessionId || systemSessionId.create();
    StaticIdSingletonClass.correlationId = this.config.correlationId || systemCorrelationId;
    StaticIdSingletonClass.apikey = this.config.apikey;
    StaticIdSingletonClass.visaSrci = visaSrci;
    StaticIdSingletonClass.debug = debug;
    StaticIdSingletonClass.isProduction = isProduction;
  },

  /**
   * setUserInfoForRecognized for DCF
   * Need to set userInfo state for recognized[SSI/ERM]user
   */
  setUserInfoForRecognized() {
    const username = getUsernameFromSsiCookie() || getUsernameFromErmCookie();
    const userInfo = {
      email: username
    };
    DcfLaunchData.setUserInfo(userInfo);
  },

  /**
   * Set Input initData to the config
   * @param {object} initData
   */
  setVisaSrciInputData(initData = {}) {
    const {
      customData, // visa srci parameter: apikey, config, domain, sessionId
      data
    } = initData;
    let error = null;
    if (!isEmptyObject(customData)) {
      this.config = customData.configs;
      this.config.apikey = customData.apikey;
      this.config.isVsb = initData.isVsb ?? false;
      this.config.isVsbButtonless = initData.isVsbButtonless ?? false;
      this.sessionId = customData.sessionId;
      this.config.sessionId = customData?.sessionId ?? null;
      this.config.correlationId = customData?.correlationId ?? null;
      this.config.visitId = data?.visitId ?? null;
      this.setIsSplunkLoggingEnabled(initData);
      const merchantCountryCode = customData?.configs?.merchantConfig?.merchantCountryCode;
      const vInitCountryCode = customData?.configs?.vInitRequest?.countryCode;
      const countryCode = vInitCountryCode || merchantCountryCode;
      this.config.consumerNationalIdentifierRequested =
        (countryCode && countryCode.toUpperCase() === 'BR') || false;
      // VCO-12821 - These values are needed for our 'euCookieInfo' redux state
      this.config.policyVersion = data?.policyVersion ?? null;
      this.config.isCookieConsentEnabled = data?.isCookieConsentEnabled ?? null;

      /**
        * VCO-14146 - set payMode (true or false). Will be added to DcfLaunchData and then automatically
        added to redux state when app loads and calls fetchConfigs
        */
      this.config.payMode =
        (data?.vInitRequest?.review?.buttonAction ?? BUTTON_ACTION.CONTINUE).toUpperCase() ===
        BUTTON_ACTION.PAY;

      if (this.config && this.config.hasOwnProperty('isInteropEligible')) {
        DcfLaunchData.setShowVdcpIno(this.config.isInteropEligible);
      }
    } else {
      error = { reason: INVALID_PARAMETER };
    }
    return error;
  },

  /**
   * Alerts the SRCi it should start/restart its session timer using the expiresIn value of the token
   * @param {number} expiresIn
   */
  startSession(expiresIn) {
    const message = { data: { expiresIn }, type: EVENTS.SYS_SESSION_START };

    PostMessage.send(message);
  },

  tncAccepted(pId) {
    const message = { data: { pId }, type: EVENTS.SYS_TNC_ACCEPTED };
    DcfLaunchData.setUserInfo({ isTncFlow: false, tncStatus: ACCEPTED });
    PostMessage.send(message);
  },

  /**
   * Deletes SSI/ERM enabled cookie
   * Invalidate below context:
   * 1. legacy secure_id_token/activity server side cookies
   * 2. new activity_ref_ssi/activity_ref server side cookies
   * 3. remember_me cookies
   * 4. idTokens
   * 5. accessToken
   * @param {object} data
   */
  unbindAppInstance(data, ignoreAccessToken = false) {
    // VCO-14997 Visa idLookup issue: Failing with different email id
    this.launchThm(true);

    if (this.accessToken) {
      data.srcCorrelationId = this.config.vIntentId || null;
      this.unbindAppInstanceB2CIAM(data, ignoreAccessToken);
    } else {
      deleteSsiErmCookies();
    }

    const message = { type: EVENTS.SYS_UNBIND_APP_INSTANCE };
    delete data.idToken;
    message.data = data;
    PostMessage.send(message);
  },

  /**
   * Helper for B2CIAM flow unbindAppInstance
   * @param {data} object
   */
  unbindAppInstanceB2CIAM(data, ignoreAccessToken) {
    if (data?.idToken) {
      this.idTokens.add(data.idToken);
    }

    const revokeArray = unbindHelpers.getB2CIAMTokenArray(this.idTokens);

    if (revokeArray.length || this.idTokens.size) {
      AccessTokenService.revokeTokens(
        this.accessToken,
        revokeArray,
        this.idTokens,
        ignoreAccessToken
      ).then(() => {
        this.idTokens = new Set();
        if (this.accessToken === undefined) {
          return;
        }

        // Need to also revoke RM cookies in ARM because
        // 1. IAM deletes cookies based on X-FORWARDED-HOST. In lower environments the full domain
        // is set for the cookies (ie. vbox671.secure.checkout.visa.com), so it will not expire the
        // remember_me cookies set by ARM which have the domain .secure.checkout.visa.com
        // 2. IAM will not delete the remember_code_vdcp cookie, only ARM will
        AccessTokenService.revokeToken(this.accessToken, REMEMBER_ME).then(() => {
          if (!ignoreAccessToken) {
            // accessToken has to be the last one to be revoked, so these 2 revocation calls are in sequential order
            AccessTokenService.revokeToken(this.accessToken, ACCESS_TOKEN);
            this.resetUserConfig();
            DcfLaunchData.setSignOut(true);
            this.accessToken = this.expiresIn = undefined;
            deleteSsiErmCookies();
          }
        });
      });
    }
  },

  /**
   * Generic method to update accessToken to the root object and update DcfLaunchData.
   * Also post messages to the SRCi that we need to start/restart our session timer.
   * @param {object} response
   */
  updateAccessToken(response, isFullAccessToken = true) {
    const accessToken = response?.data?.access_token ?? null;
    if (!isEmptyString(accessToken)) {
      //Expectation is when accessToken is present expiresIn will also be available.
      const expiresIn = response?.data?.expires_in ?? null;
      this.accessToken = accessToken;
      this.isFullAccessToken = isFullAccessToken;
      this.expiresIn = expiresIn;
      DcfLaunchData.setTokens({ expiresIn: this.expiresIn });
      this.startSession(this.expiresIn);
    }
  },

  /**
   * Update VDCP config inside system-sdk
   *
   * To avoid a possible race condition with `fetchDcfConfigs`, the configs will be 'locked' until all updates are complete
   to accommodate parallel calls to updateVDCPConfig,
   * A config id is added to `vdcpConfigsToUpdate` and then removed once the update is complete.
   * `fetchDcfConfigs` will not return configs until all configs have been removed.
   *
   * @param {object} data
   */
  async updateVDCPConfig(data) {
    const configId = `cfg-${new Date().getTime()}`;

    // VCO-13780 - race condition btwn app load and sys-sdk-ext init. Wait for config object to be create before updating configs
    if (this.config === undefined) {
      collapsedlogger(`updateVDCPConfig -- waiting for this.config to be created ${Date.now()}`);
      await until(() => this.config !== undefined);
    }

    /**
      * Adding this config to the Set of configs being updated will lock the configs.
      * If a fetchConfig is dispatched, it will wait until the all configs are cleared from
      `vdcpConfigsToUpdate` before configs are returned
      */
    this.vdcpConfigsToUpdate[configId] = true;
    collapsedlogger(
      `updateVDCPConfig -- configs updating ${Date.now()}`,
      Object.keys(this.vdcpConfigsToUpdate)
    );

    // VCO-13780 - add try/catch so that if there's an issue while updating configs, the configs will still get unlocked
    try {
      //set/update cookie country
      data = this.setCookieCountry(data);
      if (data && data.xAuthCode && (data.toRoute === CARD_LIST || data.toRoute === ADD_CARD)) {
        this.config.isCardFirstFlow = false;
        try {
          const response = await AccessTokenService.generateAccessToken(
            data.xAuthCode,
            this.sessionId
          );
          if (response.ok) {
            this.updateAccessToken(response);
            this.setProfilePreferences(response);
          } else {
            /* eslint-disable no-lonely-if */
            // VCO-14530: nullify the accessToken so account locked users can be put into guest flow in DCF.
            if (isAccountDeviceLocked(response)) {
              this.accessToken = null;
            }
            // What happens in other cases if we failed to generate an access token???
            // Should we retry the API? We're gonna need a valid token when entering SRCi.
            /* eslint-enable no-lonely-if */
          }
        } catch (err) {
          console.error(err); // eslint-disable-line no-console
        }
      }
      if (data && data.sessionId && StaticIdSingletonClass.visaSrci) {
        StaticIdSingletonClass.sessionId = data.sessionId;
        this.config.sessionId = StaticIdSingletonClass.sessionId;
        this.sessionId = StaticIdSingletonClass.sessionId;
      }
      if (data && data.toRoute) {
        this.config.toRoute = data.toRoute;
        this.config.showCardList = data.toRoute === CARD_LIST;
      }
      if (data && data.countryCode) {
        DcfLaunchData.setUserInfo({
          countryCode: data.countryCode
        });
      }
      if (data && data.locale) {
        DcfLaunchData.setUserInfo({
          locale: data.locale
        });
      }
      if (data.hasOwnProperty('prefillData') && data.cardBrand === 'VISA') {
        const prefillConsent = data.prefillData.isUserConsentedPrefill;
        if (prefillConsent) {
          DcfLaunchData.setUserInfo({
            email: data.prefillData.email,
            firstName: data.prefillData.firstName,
            isUserConsentedPrefill: prefillConsent,
            lastName: data.prefillData.lastName,
            phone: data.prefillData.phone
          });
        }
      }
      if (data && data.cookieCountry) {
        DcfLaunchData._customProps.cookieCountry = data.cookieCountry;
      }
      if (data && data.cookieLocale) {
        DcfLaunchData._customProps.cookieLocale = data.cookieLocale;
      }
      if (data && data.dcfActionCode) {
        DcfLaunchData.setDcfActionCode(data.dcfActionCode);
      }
      if (data && data.why) {
        this.config.why = data.why;
      }
      if (data && 'showVdcpIno' in data) {
        DcfLaunchData.setShowVdcpIno(data.showVdcpIno);
      }
      if (data && 'signOut' in data) {
        DcfLaunchData.setSignOut(data.signOut);
      }
      if (data && data.sdkCerts) {
        this.config.sdkCerts = data.sdkCerts;
      }
      if (data && data.dataLayer) {
        this.config.dataLayer = data.dataLayer;
      }
      if (data && data.grant) {
        DcfLaunchData.setGrantInfo(data.grant);
      }
      if (data && 'isLocked' in data) {
        DcfLaunchData.setUserInfo({ isLocked: data.isLocked });
      }
      if (data?.isUserAuthenticated) {
        DcfLaunchData.setUserInfo({ isUserAuthenticated: data.isUserAuthenticated });
      }
      if (data && data.idValidationSessionId) {
        DcfLaunchData._customProps.idValidationSessionId = data.idValidationSessionId;
      }
      if (data && 'isTncFlow' in data) {
        DcfLaunchData.setUserInfo({ isTncFlow: data.isTncFlow });
      }

      // VCO-12837 – In some cases the dcf may call setIntent but this intentId is not passed back to the system-sdk.
      if (!this.config.vIntentId && data && data.intentId) {
        this.config.vIntentId = data.intentId;
      }

      logger('updateVDCPConfig:: config updated');
    } catch (e) {
      console.error('Error updating configs', e); // eslint-disable-line no-console
    }

    // done updating configs, so remove the config
    delete this.vdcpConfigsToUpdate[configId];
    collapsedlogger(
      `updateVDCPConfig -- config update complete for ${configId}`,
      Array.from(this.vdcpConfigsToUpdate).join(' ')
    );
  },

  /**
   * When `updateVDCPConfig` is called, it will generate an ID and add it to this object.
   * Once the update is complete, it will delete the id.
   * This will contain all IDs of configs currently being updated. If it has values, configs are considered 'locked'
   */
  vdcpConfigsToUpdate: {}
};

export const _receivePostMessage = event => {
  if (!event.data || typeof event.data !== 'string') {
    return;
  }

  // original code splits and then looks for array.length > 2
  if (event.data.indexOf(DATA_DELIM) < -1) {
    return;
  }

  const rawMessage = event.data.split(DATA_DELIM);
  // Ensure that message is parsed correctly, eg. handle API keys with --
  const message = fixMessage([rawMessage[0]], rawMessage[1], rawMessage.slice(2), DATA_DELIM);

  let data = {};
  collapsedlogger(message[0], message[1]);
  try {
    data = JSON.parse(message[1]);
    if (event.origin === _getSrciDomain()) {
      switch (message[0]) {
        case EVENTS.SYS_APP_LOADED:
          didDcfAskForConfigs = true;
          if (isConfigsReadyForDcf) {
            PostMessage.send(getRouteUserInDCFMessage());
          }
          break;
        case EVENTS.SYS_DELETE_TOKENS:
          Adapter.accessToken = Adapter.expiresIn = undefined;
          DcfLaunchData.setXAuthCode(null);
          break;
        case EVENTS.SYS_MERCHANT_TRANSACTION_CANCEL:
          PostMessage.send({ data, type: 'cancel' });
          break;
        case EVENTS.SYS_MERCHANT_TRANSACTION_SUCCESS:
          if (!StaticIdSingletonClass.visaSrci && data.unbindAppInstance) {
            Adapter.unbindAppInstance({ idToken: data.idToken });
          }
          PostMessage.send({ data, type: 'success' });
          break;
        case EVENTS.SYS_SIGN_OUT:
          // Call unbind with below conditions
          // 1. non visa srci && visa dcf: StaticIdSingletonClass.visaSrci = false
          // 2. visa srci OTP screen on click not you only place where data.isDCF explicitly set to false
          if (!StaticIdSingletonClass.visaSrci || data.isDCF === false) {
            Adapter.unbindAppInstance({ idToken: data.idToken });
            Adapter.updateVDCPConfig({ signOut: true });
          }
          break;
        case EVENTS.SYS_UPDATE_VDCP_CONFIG:
          Adapter.updateVDCPConfig(data);
          break;
        case EVENTS.SYS_UPDATE_VINIT_CONFIG:
          Adapter.pushUpdatedVinit(data);
          break;
        case EVENTS.SYS_INIT:
          Adapter.init(data.initData, data.pId);
          break;
        case EVENTS.SYS_IS_RECOGNIZED:
          Adapter.isRecognized(data);
          break;
        case EVENTS.SYS_IDENTITY_LOOKUP:
          const { branding, ...identityRequest } = data;
          const pIdParam = data.pId;
          //make sure pId is not included with identityRequest
          delete identityRequest.pId;
          Adapter.identityLookup({
            branding,
            identityLookUpParams: { consumerIdentity: identityRequest },
            pId: pIdParam
          });
          break;
        case EVENTS.SYS_INITIATE_IDENTITY_VALIDATION:
          Adapter.initiateIdentityValidation(data.pId);
          break;
        case EVENTS.SYS_COMPLETE_IDENTITY_VALIDATION:
          Adapter.completeIdentityValidation(data, data.pId);
          break;
        case EVENTS.SYS_CARD_BINS:
          Adapter.getCardBins(data.pId);
          break;
        case EVENTS.SYS_GET_SRC_PROFILE:
          Adapter.getSrcProfileByIdTokens(data.pId, data.srcProfileParam);
          break;
        case EVENTS.SYS_HANDLE_RESPONSE:
          Adapter.handleResponse(data);
          break;
        case SRC_DCF_CONSTANTS.EVENTS.DCF_FETCHED_CONFIGS:
          Adapter.fetchDcfConfigs(data.pId);
          break;
        case SRC_DCF_CONSTANTS.EVENTS.DCF_FETCH_CONFIGS:
          Adapter.fetchSendConfigsToDCF(data.pId);
          break;
        case SRC_DCF_CONSTANTS.EVENTS.FETCH_RXO_MERCHANT_CONFIGS:
          Adapter.fetchSendMerchantConfigsToRXO(data.pId);
          break;
        case SRC_DCF_CONSTANTS.EVENTS.DCF_PURCHASE_INTENT_FAILED:
        case SRC_DCF_CONSTANTS.EVENTS.DCF_PURCHASE_INTENT_SUCCESS:
          Adapter.handleUpdateIntent(data);
          break;
        case SRC_DCF_CONSTANTS.EVENTS.DCF_GOTO_CARD_LIST:
          Adapter.goToInitiator(data.pId, CARD_LIST, null);
          break;
        case EVENTS.SYS_SESSION_RENEW:
          Adapter.renewSession(data.pId);
          break;
        case EVENTS.SYS_SESSION_TIMEOUT:
          Adapter.sessionTimeout(data.pId, data.why);
          break;
        case EVENTS.SYS_GOTO_INITIATOR:
          Adapter.goToInitiator(data.pId, data.toRoute, data.why, data.customData);
          break;
        case EVENTS.SYS_TXN_SUCCESS:
          Adapter.handleUpdateIntent(data);
          // call unbind with below conditions
          // 1. non visa srci && visa dcf && complete transaction with checkbox unchecked
          if (data?.unbindAppInstance && !StaticIdSingletonClass.visaSrci) {
            Adapter.unbindAppInstance({});
          }
          break;
        case EVENTS.SYS_CHECKOUT:
          //needed to update userinfo state for app to show locale related behavior
          Adapter.updateVDCPConfig(data);
          Adapter.checkout(data);
          break;
        case EVENTS.SYS_DELETE_INTENT:
          const { pId, suppressDeleteIntentApi } = data;
          Adapter.deleteIntent(pId, suppressDeleteIntentApi);
          break;
        case EVENTS.SYS_PUSH_GTM_DATA:
          GtmHelper.pushGtmData(data);
          break;
        case EVENTS.SYS_TNC_ACCEPTED:
          Adapter.tncAccepted(data.pId);
          break;
        case EVENTS.SYS_PUSH_EVENTS:
          Adapter.pushEvents(data);
          break;
        case EVENTS.SYS_RESET_USER_CONFIG:
          Adapter.resetUserConfig();
          Adapter.accessToken = Adapter.expiresIn = undefined;
          break;
        case EVENTS.SYS_UNBIND_APP_INSTANCE:
          Adapter.updateVDCPConfig({ signOut: true });
          // Call unbind with below conditions
          // 1. VCO.unbindAppInstance() is called from Visa-sdk
          Adapter.unbindAppInstance(data);
          break;
        case EVENTS.SYS_SRCI_LOADED:
          // If user has ERM or SSI cookies, THM will have already been loaded by the merchant SDK.
          if (!isErmEnabled() && !hasValidStaySignedIn()) {
            Adapter.launchThm();
          }

          srciLoaded = true;
          Adapter.fetchDeviceId();
          break;
        // TODO: F19043 - redact on Phase 2+3 Performance Pass
        case EVENTS.SYS_SET_THM_PROFILE_ID:
          thmProfilingComplete = true;
          Adapter.fetchDeviceId();
          break;

        case SRC_DCF_CONSTANTS.EVENTS.DCF_SEND_VBA_TOKEN:
          Adapter.getVbaToken()
            .then(token => {
              PostMessage.send({
                data: { pId: data?.pId, token },
                type: SRC_DCF_CONSTANTS.EVENTS.DCF_SEND_VBA_TOKEN
              });
            })
            .catch(() => {
              PostMessage.send({
                data: { pId: data?.pId, token: null },
                type: SRC_DCF_CONSTANTS.EVENTS.DCF_SEND_VBA_TOKEN
              });
            });
          break;
      }
    }
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
};

if (window.removeEventListener) {
  window.removeEventListener('message', _receivePostMessage);
} else if (window.detachEvent) {
  window.detachEvent('onmessage', _receivePostMessage);
}

if (window.addEventListener) {
  window.addEventListener('message', _receivePostMessage);
} else if (window.attachEvent) {
  window.attachEvent('onmessage', _receivePostMessage);
}
